import React from 'react';

import PropTypes from 'prop-types';
import { Header, Message } from 'semantic-ui-react';

import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { useDispatch } from 'react-redux';

import { flagBusinessSaved } from 'actions/business';
import PlaceActionsTable from 'components/modules/business-profile/components/PlaceActionsTable';
import { PlaceActionsContextProvider } from 'components/modules/business-profile/contexts/PlaceActions.context';

import {
  ACTION_DELETE,
  ACTION_SET,
  NO_SETTINGS_WARNING_MESSAGE,
  PLACE_ACTIONS_PUSH_NOT_ENABLED_WARNING_MESSAGE,
  ACTIONS_SECTION_DESCRIPTION,
} from '../../BusinessInfoTab.constants';
import { getNextPayload } from '../../BusinessInfoTab.utils';

import './PlaceActionsForm.scss';

const propTypes = {
  locationSetting: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    serviceData: PropTypes.shape({}),
  }),
  isPlaceActionsPushEnabled: PropTypes.bool.isRequired,
  providerEntityAttributes: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  getProviderEntityAttributes: PropTypes.func.isRequired,
  updateProviderEntityAttribute: PropTypes.func.isRequired,
};

const defaultProps = {
  locationSetting: null,
};

export default function PlaceActionsForm({
  locationSetting,
  isPlaceActionsPushEnabled,
  providerEntityAttributes,
  getProviderEntityAttributes,
  updateProviderEntityAttribute,
}) {
  const dispatch = useDispatch();

  async function updatePlaceAction(id, { action, newData = {} } = {}) {
    const currentProviderEntityAttributes = await getProviderEntityAttributes();

    const previousPlaceAction = currentProviderEntityAttributes.find(
      (providerEntityAttribute) => providerEntityAttribute.id === id,
    );

    const payload = getNextPayload(action, newData, previousPlaceAction);

    await updateProviderEntityAttribute(id, payload);
    dispatch(flagBusinessSaved(true));
  }

  async function onSavePlaceAction(newData) {
    const { id, isPreferred } = newData;

    await updatePlaceAction(id, { action: ACTION_SET, newData });

    if (isPreferred) {
      // Unset the previous preferred place action
      const previousPreferredPlaceAction = providerEntityAttributes.find(
        (current) => _get(current, 'attributes.isPreferred') && _get(current, 'id') !== id,
      );
      if (previousPreferredPlaceAction) {
        await updatePlaceAction(previousPreferredPlaceAction.id, {
          action: ACTION_SET,
          newData: {
            pageId: _get(previousPreferredPlaceAction, 'objectId'),
            uri: _get(previousPreferredPlaceAction, 'attributes.uri'),
            isPreferred: false,
          },
        });
      }
    }
  }

  async function onClearPlaceAction(placeActionId) {
    await updatePlaceAction(placeActionId, {
      action: ACTION_DELETE,
    });
  }

  const contextValue = {
    providerEntityAttributes,
    onSavePlaceAction,
    onClearPlaceAction,
  };

  return (
    <PlaceActionsContextProvider value={contextValue}>
      <div className="place-actions-form">
        <Header as="h3" className="section-header">
          Place Actions
        </Header>
        <Message className="section-description" content={ACTIONS_SECTION_DESCRIPTION} />

        {_isEmpty(locationSetting) && (
          <Message
            warning
            className="no-settings-for-location"
            content={NO_SETTINGS_WARNING_MESSAGE}
          />
        )}

        {!_isEmpty(locationSetting) && !isPlaceActionsPushEnabled && (
          <Message
            warning
            className="place-actions-push-not-enabled"
            content={PLACE_ACTIONS_PUSH_NOT_ENABLED_WARNING_MESSAGE}
          />
        )}

        {!_isEmpty(locationSetting) && isPlaceActionsPushEnabled && (
          <>
            <PlaceActionsTable />
          </>
        )}
      </div>
    </PlaceActionsContextProvider>
  );
}

PlaceActionsForm.propTypes = propTypes;
PlaceActionsForm.defaultProps = defaultProps;
